import z from 'zod'
import { decimalSchema } from '../../utils/schema'

const depositHistorySchema = z.object({
  /** Order ID */
  orderId: z.string(),
  /** トランザクション ID（`dest` が `on_chain` の場合はオンチェーンハッシュ、`internal_transfer` の場合は trade ID） */
  tradeId: z.string(),
  /** トークン名 */
  coin: z.string(),
  /** 種類（固定値: `deposit`） */
  type: z.string(),
  /** 数量 */
  size: decimalSchema,
  /** 入金ステータス（`pending`: 確認待ち, `fail`: 失敗, `success`: 成功） */
  status: z.union([
    z.literal('pending'),
    z.literal('fail'),
    z.literal('success'),
  ]),
  /** 入金元アドレス（`on_chain`: オンチェーンアドレス, `internal_transfer`: UID, email, または携帯番号） */
  fromAddress: z.string().nullable().optional(),
  /** 入金先アドレス（`on_chain`: オンチェーンアドレス, `internal_transfer`: UID, email, または携帯番号） */
  toAddress: z.string(),
  /** 入金ネットワーク（`internal_transfer` の場合は無視） */
  chain: z.string(),
  /** 入金タイプ（`on_chain`: オンチェーン, `internal_transfer`: 内部転送） */
  dest: z.union([z.literal('on_chain'), z.literal('internal_transfer')]),
  /** 作成時間（ミリ秒） */
  cTime: z.string(),
  /** 編集時間（ミリ秒） */
  uTime: z.string(),
})

export const responseSchema = z.object({
  /** Response code, `00000` for success */
  code: z.string(),
  /** Response message, `success` for success */
  msg: z.string(),
  /** Request time in milliseconds */
  requestTime: z.union([
    z
      .string()
      .regex(/^\d+$/)
      .transform((v) => Number.parseInt(v)),
    z.number(),
  ]),
  /** API のレスポンスデータ配列 */
  data: z.array(depositHistorySchema),
})

/** timestamp を string / number どちらで返されても string へ揃える。 */
const timestampSchema = z.union([
  z.string().regex(/^\d+$/),
  z.number().transform((value) => value.toString()),
])

/**
 * Unified Account (v3) `/api/v3/account/deposit-records` の 1 件分。
 * v2 との差分は 3 つの改名のみのため、下流処理を変えずに済むよう v2 の形へ正規化する。
 * - `tradeId` -> `recordId` (いずれも on_chain の場合はトランザクションハッシュ)
 * - `cTime` -> `createdTime`
 * - `uTime` -> `updatedTime`
 */
const depositHistoryV3Schema = z
  .object({
    /** Order ID */
    orderId: z.string(),
    /** v2 の tradeId に相当。`dest` が `on_chain` の場合はオンチェーンハッシュ */
    recordId: z.string(),
    /** トークン名 */
    coin: z.string(),
    /** 種類（固定値: `deposit`） */
    type: z.string(),
    /** 数量 */
    size: decimalSchema,
    /** 入金ステータス */
    status: z.union([
      z.literal('pending'),
      z.literal('fail'),
      z.literal('success'),
    ]),
    /** 入金元アドレス */
    fromAddress: z.string().nullable().optional(),
    /** 入金先アドレス */
    toAddress: z.string(),
    /** 入金ネットワーク */
    chain: z.string(),
    /** 入金タイプ */
    dest: z.union([z.literal('on_chain'), z.literal('internal_transfer')]),
    /** 作成時間（ミリ秒） */
    createdTime: timestampSchema,
    /** 更新時間（ミリ秒） */
    updatedTime: timestampSchema,
  })
  .transform((row) => ({
    ...row,
    tradeId: row.recordId,
    cTime: row.createdTime,
    uTime: row.updatedTime,
  }))

export const responseSchemaV3 = z.object({
  /** Response code, `00000` for success */
  code: z.string(),
  /** Response message, `success` for success */
  msg: z.string(),
  /** Request time in milliseconds */
  requestTime: z.union([
    z
      .string()
      .regex(/^\d+$/)
      .transform((v) => Number.parseInt(v)),
    z.number(),
  ]),
  /** API のレスポンスデータ配列 */
  data: z.array(depositHistoryV3Schema),
})
